import { useLocalParticipant, type TrackReferenceOrPlaceholder } from "@livekit/components-react";
import { Track } from "livekit-client";
import { Maximize2, Mic, MicOff, UserPlus, Video, VideoOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { DeviceToggles } from "../hooks/useDeviceToggles";
import { useSpeakerStage } from "../hooks/useSpeakerStage";
import { CallTile } from "./CallTile";
import { HangUpIcon } from "./HangUpIcon";

interface CallPipWindowProps {
  /** Guests waiting to be admitted; > 0 shows the top badge. */
  waitingCount: number;
  /** Bring the full call back (focus the tab, open /call/<slug>). */
  onReturn: () => void;
  /** Deliberately end the call. */
  onEnd: () => void;
  toggles: DeviceToggles;
}

const ROUND_BTN =
  "size-10 rounded-full flex items-center justify-center transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70";

/**
 * The content of the floating Document Picture-in-Picture window: the main
 * speaker (never the viewer while anyone else has a tile), a small self
 * preview, and the four essentials — mic, camera, back to the call, end.
 *
 * Rendered by CallPipHost through a portal into the PiP window's body, so it
 * shares the call's React context (LiveKit room, router, i18n) while living
 * in a different document. It deliberately paints its own dark surface: the
 * floating window sits over arbitrary apps, where a light tile would vanish.
 */
export function CallPipWindow({ waitingCount, onReturn, onEnd, toggles }: CallPipWindowProps) {
  const { t } = useTranslation();
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled, cameraTrack } =
    useLocalParticipant();
  const { mainTrack } = useSpeakerStage({ excludeLocalFromMain: true });

  const mainParticipant = mainTrack?.participant;
  const mainIsLocal = !!mainParticipant?.isLocal;
  const speakerName = mainParticipant
    ? mainParticipant.name?.trim() || mainParticipant.identity.split("@")[0]
    : "";
  const showSelf = !!mainTrack && !mainIsLocal && isCameraEnabled && !!localParticipant;
  const selfRef = {
    participant: localParticipant,
    source: Track.Source.Camera,
    publication: cameraTrack,
  } as TrackReferenceOrPlaceholder;

  const micLabel = isMicrophoneEnabled ? t("call.controls.muteMic") : t("call.controls.unmuteMic");
  const camLabel = isCameraEnabled
    ? t("call.controls.turnOffCamera")
    : t("call.controls.turnOnCamera");
  const backLabel = t("call.pip.backToCall");
  const endLabel = t("call.controls.leaveCall");

  return (
    <div className="h-screen w-screen bg-[#14171f] text-white flex flex-col gap-2 p-2 overflow-hidden select-none">
      <div className="relative flex-1 min-h-0">
        {mainTrack ? (
          <CallTile trackRef={mainTrack} size="sm" hideNamePill className="h-full w-full" />
        ) : (
          <div className="h-full w-full rounded-2xl bg-white/5" />
        )}

        {mainTrack && !mainIsLocal && speakerName && (
          <div className="absolute bottom-2 start-2 max-w-[calc(100%-1rem)] px-2.5 py-1 rounded-lg bg-black/55 backdrop-blur-sm text-xs font-semibold truncate">
            {t("call.pip.speaking", { name: speakerName })}
          </div>
        )}

        {showSelf && (
          <div className="absolute top-2 end-2 w-[30%] min-w-16 aspect-video">
            <CallTile trackRef={selfRef} size="sm" hideNamePill className="h-full w-full shadow-lg" />
            <span className="absolute bottom-1 start-1 px-1.5 rounded bg-black/55 text-[10px] font-bold">
              {t("call.pip.you")}
            </span>
          </div>
        )}

        {waitingCount > 0 && (
          <button
            type="button"
            onClick={onReturn}
            className="absolute top-2 start-2 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            style={{ background: "var(--accent)", color: "var(--color-primary-foreground, #000)" }}
          >
            <UserPlus className="size-3.5" />
            {t("call.pip.waiting", { count: waitingCount })}
          </button>
        )}
      </div>

      <div className="shrink-0 flex items-center justify-center gap-2.5 pb-1">
        <button
          type="button"
          aria-label={micLabel}
          title={micLabel}
          aria-pressed={!isMicrophoneEnabled}
          onClick={() => void toggles.toggleMic(!isMicrophoneEnabled)}
          className={`${ROUND_BTN} ${isMicrophoneEnabled ? "bg-white/10" : "bg-red-500"}`}
        >
          {isMicrophoneEnabled ? <Mic className="size-4" /> : <MicOff className="size-4" />}
        </button>
        <button
          type="button"
          aria-label={camLabel}
          title={camLabel}
          aria-pressed={!isCameraEnabled}
          onClick={() => void toggles.toggleCamera(!isCameraEnabled)}
          className={`${ROUND_BTN} ${isCameraEnabled ? "bg-white/10" : "bg-red-500"}`}
        >
          {isCameraEnabled ? <Video className="size-4" /> : <VideoOff className="size-4" />}
        </button>
        <button
          type="button"
          aria-label={backLabel}
          title={backLabel}
          onClick={onReturn}
          className={ROUND_BTN}
          style={{ background: "var(--accent)", color: "var(--color-primary-foreground, #000)" }}
        >
          <Maximize2 className="size-4" />
        </button>
        <button
          type="button"
          aria-label={endLabel}
          title={endLabel}
          onClick={onEnd}
          className="h-10 w-14 rounded-full flex items-center justify-center bg-red-500 transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        >
          <HangUpIcon className="size-4" />
        </button>
      </div>
    </div>
  );
}
